/**
 * Design Tokens for SignalIQ
 *
 * These constants provide design tokens for use in inline styles when Tailwind
 * utilities are not sufficient (e.g., dynamic styles, complex calculations).
 *
 * For static styles, prefer Tailwind utilities (e.g., className="text-neutral-900")
 * For dynamic styles, use these constants (e.g., style={{ color: colors.neutral[900] }})
 */

// ============================================================================
// COLORS
// ============================================================================

export const colors = {
  gray: {
    0: '#FFFFFF',
    50: '#F7F7F9',
    100: '#F1F2F4',
    200: '#E6E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },

  brand: {
    50: '#D4E1F6',
    400: '#4A78E7',
    500: '#243CE3',
  },

  primary: {
    50: '#F7F7F9',
    100: '#F1F2F4',
    200: '#E6E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },

  neutral: {
    50: '#F7F7F9',
    100: '#F1F2F4',
    200: '#E6E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },

  success: {
    50: '#F0FDF4',
    100: '#DCFCE7',
    200: '#BBF7D0',
    300: '#86EFAC',
    400: '#4ADE80',
    500: '#22C55E',
    600: '#16A34A',
    700: '#15803D',
    800: '#166534',
    900: '#14532D',
  },

  warning: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
  },

  error: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
  },

  // Chart-specific semantic colors (Tailwind equivalents for SVG/Recharts usage)
  chart: {
    blue: '#2563EB',       // blue-600
    purple: '#9333EA',     // purple-600
    green: '#16A34A',      // green-600
    red: '#DC2626',        // red-600
    amber: '#D97706',      // amber-600
    emerald: '#059669',    // emerald-600
    cyan: '#0891B2',       // cyan-600
  },

  code: {
    bg: '#262423',
    fg: '#E5E7EB',
    muted: '#9CA3AF',
  },

  background: {
    light: '#F8FAFC',
    lighter: '#FAFBFC',
  },

  white: '#FFFFFF',
  black: '#000000',
} as const;

/**
 * Semantic colors for sidebar/public view components
 * Shared between PublicDatasetView and PublicViewSidebar
 */
export const sidebarColors = {
  bg: {
    sidebar: '#F4F5F7',
    sidebarHover: '#EAEBEF',
    selected: '#FFFFFF',
    white: '#FFFFFF',
  },
  border: {
    light: '#E5E7EB',
    lighter: '#F1F2F4',
  },
  text: {
    primary: '#1F2937',
    secondary: '#4B5563',
    muted: '#6B7280',
    sectionHeader: '#9CA3AF',
  },
} as const;

/**
 * Skylarq desktop app brand colors (from assistant.skylarq.ai)
 * Used for desktop download banners and promo modals
 */
export const skylarqBrand = {
  accent: '#C45D35',
  accentHover: '#D4846A',
  accentGlow: 'rgba(196,93,53,0.08)',
  accentShadow: '0 16px 48px rgba(196,93,53,0.3)',
  bg: '#FFFCF9',
  bgShifted: '#F8F5F0',
  text: '#1A1815',
  textSoft: '#3D3A36',
  textMuted: '#6B6660',
  textFaint: '#B8B2A6',
  border: 'rgba(26,24,21,0.05)',
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const fontSize = {
  '2xs': '11px',
  xs: '12px',
  '13': '13px',
  sm: '14px',
  base: '16px',
  lg: '18px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
} as const;

export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const lineHeight = {
  tight: 1.3,
  normal: 1.4,
  relaxed: 1.5,
} as const;

export const letterSpacing = {
  tighter: '-0.01em',
  normal: '0',
} as const;

export const fontFamily = {
  sans: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, sans-serif',
  serif: 'Georgia, ui-serif, serif',
};

// ============================================================================
// SPACING
// ============================================================================

export const spacing = {
  0: '0px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  4: '16px',
  4.5: '18px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  14: '56px',
  16: '64px',
  20: '80px',
} as const;

// ============================================================================
// BORDERS
// ============================================================================

export const borderRadius = {
  sm: '6px',
  default: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '50%',
} as const;

export const borderWidth = {
  0: '0px',
  1: '1px',
  2: '2px',
  4: '4px',
} as const;

// ============================================================================
// SHADOWS
// ============================================================================

export const boxShadow = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  default: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  popover: '0 8px 24px rgba(0, 0, 0, 0.12)',
  modal: '0 16px 48px rgba(0, 0, 0, 0.18)',
  focusRing: '0 0 0 2px rgba(17, 24, 39, 0.2)',
  focusPrimary: '0 0 0 3px rgba(17, 24, 39, 0.08)',
  focusPrimaryStrong: '0 0 0 2px rgba(17, 24, 39, 0.15)',
  card: '0 1px 2px rgba(0, 0, 0, 0.05)',
  cardHover: '0 2px 4px rgba(0, 0, 0, 0.1)',
  panel: '-2px 0 8px rgba(0, 0, 0, 0.04)',
  dropdown: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
} as const;

// ============================================================================
// TRANSITIONS
// ============================================================================

export const transition = {
  duration: {
    fast: '100ms',
    default: '150ms',
    slow: '200ms',
    panel: '280ms',
    slower: '300ms',
  },
  durationMs: {
    fast: 100,
    default: 150,
    slow: 200,
    panel: 280,
    slower: 300,
  },
  timing: {
    default: 'ease-out',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
    linear: 'linear',
    panel: 'cubic-bezier(0.16, 1, 0.3, 1)',
  },
} as const;

export const transitions = {
  all: 'all 150ms ease-out',
  colors: 'background-color 150ms ease-out, color 150ms ease-out, border-color 150ms ease-out',
  transform: 'transform 150ms ease-out',
  opacity: 'opacity 150ms ease-out',
} as const;

// ============================================================================
// COMMON STYLE OBJECTS
// ============================================================================

export const commonStyles = {
  modalOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    position: 'fixed' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },

  focusRing: {
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(17, 24, 39, 0.2)',
  },

  customScrollbar: {
    scrollbarWidth: 'thin' as const,
    scrollbarColor: `${colors.neutral[300]} transparent`,
  },

  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },

  spinner: {
    border: `3px solid ${colors.neutral[200]}`,
    borderTopColor: colors.primary[900],
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const border = (color: string, width: string = '1px') => ({
  border: `${width} solid ${color}`,
});

export const borderSide = (
  side: 'top' | 'right' | 'bottom' | 'left',
  color: string,
  width: string = '1px'
) => ({
  [`border${side.charAt(0).toUpperCase()}${side.slice(1)}`]: `${width} solid ${color}`,
});

export const padding = (size: keyof typeof spacing | string) => ({
  padding: typeof size === 'number' && size in spacing ? spacing[size as keyof typeof spacing] : size,
});

export const margin = (size: keyof typeof spacing | string) => ({
  margin: typeof size === 'number' && size in spacing ? spacing[size as keyof typeof spacing] : size,
});
